import { useHistory } from 'react-router-dom';
import { SupervisedUserCircleOutlined } from '@mui/icons-material';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { MutationStatus } from 'models/enums';
import useMutationStatus from 'hooks/useMutationStatus';
import MutationKeys from 'hooks/mutations/keys';
import useContacts from 'hooks/queries/useContacts';
import { Stack, Tooltip } from '@mui/material';
import Button from 'domains/core/components/Button';
import DropdownButton from 'domains/core/components/DropdownButton';
import EmptyStateLayout from 'domains/core/components/EmptyStateLayout';
import PageLayout from 'domains/core/components/PageLayout';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import SuccessSnackbar from 'domains/core/components/Snackbars/SuccessSnackbar';
import ContactsUploadDialog from 'domains/contacts/components/ContactsUploadDialog';
import FileUploadButton from 'domains/contacts/components/FileUploadButton';
import useContactsFileUploadProps from 'domains/contacts/hooks/useContactsFileUploadProps';
import SegmentsTable from '../SegmentsTable';
import { DropdownButtonOption } from 'domains/core/components/DropdownButton/DropdownButton';
import { Props as SegmentTableProps } from '../SegmentsTable/SegmentsTable';
import { Segment } from 'domains/segments/types';
import { Folder } from 'models/types';
import { UseQueryResult } from 'react-query';
import useSegmentQueryManager from 'hooks/useSegmentQueryManager';
import { getSegmentCreatePath } from 'domains/segments/utils';
import SearchField from 'domains/core/components/SearchField';

export type Props = {
    createButtonDropdownOptions?: DropdownButtonOption[];
    isUserOnRootLevel: boolean;
    segmentsQuery: SegmentTableProps<Segment | Folder>['segmentsQuery'] & UseQueryResult<(Segment | Folder)[], Error>;
    isArchivedView?: boolean;
    onToggleArchivedView?: () => void;
    searchTerm?: string;
    onSearchTermChange?: (value: string) => void;
};

const SegmentsPageLayout = ({
    createButtonDropdownOptions,
    isUserOnRootLevel,
    segmentsQuery,
    isArchivedView = false,
    onToggleArchivedView,
    searchTerm,
    onSearchTermChange,
}: Props) => {
    const history = useHistory();
    const { folderId: parentFolderId } = useSegmentQueryManager();

    const {
        data: contacts,
        error: contactsError,
        isError: isContactsError,
        isSuccess: isContactsSuccess,
    } = useContacts({ options: { limit: '1' } });

    const contactsFileUploadProps = useContactsFileUploadProps();

    const createAudienceStatus = useMutationStatus(MutationKeys.CREATE_AUDIENCE);
    const createSegmentStatus = useMutationStatus(MutationKeys.CREATE_SEGMENT);
    const createFolderStatus = useMutationStatus(MutationKeys.CREATE_SEGMENT_FOLDER);

    const { data: segmentsData, error, isError, isSuccess } = segmentsQuery;

    const isEmpty = isArchivedView ? isSuccess && !segmentsData?.length : isContactsSuccess && !contacts?.length;

    const getHeaderAction = () => {
        const icon = <SupervisedUserCircleOutlined />;
        if (isEmpty || isArchivedView) return;

        if (!!createButtonDropdownOptions && isUserOnRootLevel) {
            return (
                <DropdownButton
                    ariaLabel="choose what to create"
                    data-testid="audience-create-button-dropdown"
                    icon={icon}
                    label="Create"
                    options={createButtonDropdownOptions}
                    size="medium"
                    variant="outlined"
                />
            );
        }

        return (
            <Button
                data-testid="audience-create-button"
                endIcon={icon}
                onClick={() => history.push(getSegmentCreatePath(parentFolderId))}
                variant="outlined"
            >
                Create
            </Button>
        );
    };

    const renderPageBody = () => {
        if (isEmpty && isUserOnRootLevel) {
            return (
                <EmptyStateLayout
                    caption={isArchivedView ? 'archived audience' : 'audience'}
                    header={isArchivedView ? 'no archived audiences' : 'build your audience'}
                    guidingText={
                        isArchivedView
                            ? "You don't have any archived audiences."
                            : 'Once your contacts are uploaded, start segmenting your contacts into audiences to reach the most relevant recipients'
                    }
                >
                    {!isArchivedView && (
                        <>
                            <ContactsUploadDialog {...contactsFileUploadProps} />
                            <FileUploadButton {...contactsFileUploadProps} />
                        </>
                    )}
                </EmptyStateLayout>
            );
        }

        return (
            <SegmentsTable segmentsQuery={segmentsQuery} isNestedContent={!isUserOnRootLevel} searchTerm={searchTerm} />
        );
    };

    const renderHeaderOthers = () => {
        if (!onToggleArchivedView && !onSearchTermChange) return;

        return (
            <Stack direction="row" spacing={0} alignItems="center">
                {onToggleArchivedView && (
                    <Tooltip
                        key={`audience-archive-tooltip-${isArchivedView}`}
                        title={isArchivedView ? 'Original audience' : 'Archive list'}
                    >
                        <Button
                            onClick={onToggleArchivedView}
                            data-testid="archive-toggle-button"
                            sx={{ minWidth: 'auto', ml: -2 }} // Move icon further left
                        >
                            {isArchivedView ? <UnarchiveIcon /> : <ArchiveIcon />}
                        </Button>
                    </Tooltip>
                )}
                {onSearchTermChange && (
                    <SearchField
                        placeholder="Search audiences..."
                        value={searchTerm || ''}
                        onChange={onSearchTermChange}
                        dataTestId="audience-search-input"
                    />
                )}
            </Stack>
        );
    };

    return (
        <PageLayout
            header={isArchivedView ? 'archived audiences' : 'audiences'}
            headerIcon={<SupervisedUserCircleOutlined fontSize="inherit" />}
            headerAction={getHeaderAction()}
            headerOthers={renderHeaderOthers()}
        >
            {(createAudienceStatus === MutationStatus.SUCCESS || createSegmentStatus === MutationStatus.SUCCESS) && (
                <SuccessSnackbar successMessage="Audience saved successfully." />
            )}
            {createFolderStatus === MutationStatus.SUCCESS && (
                <SuccessSnackbar successMessage="Folder created successfully." />
            )}
            {isContactsError && <ErrorSnackbar errorMessage={contactsError?.message} />}
            {isError && <ErrorSnackbar errorMessage={error?.message} />}
            {isSuccess && renderPageBody()}
        </PageLayout>
    );
};

export default SegmentsPageLayout;
